import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Tag, ChevronDown, Plus, Check, Lock, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useConversationStore, isSystemLabelKey } from '@/stores/conversation-store';
import { usePermissions } from '@/hooks/use-permissions';
import { findAutomationsUsingLabel } from '@/lib/automation-label-usage';
import type { ConvChatLabel } from '@/types/messaging';

const PRESET_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#64748b', '#78716c',
];

interface ConvLabelPickerProps {
  conversationId: string;
  currentLabel?: string;
  /** Compact mode for conversation list — small inline badge */
  compact?: boolean;
  /** Called after the user picks or creates a label for this conversation. */
  onChange?: (label: string) => void;
}

export function ConvLabelPicker({ conversationId, currentLabel, compact, onChange }: ConvLabelPickerProps) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  // Which label's delete popup is open. An in-app ConfirmDialog instead of
  // window.confirm — the app runs inside cross-origin CRM/WhatsApp iframes
  // where native modal dialogs can be suppressed.
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
  // Automations that still use the label in the popup (looked up when it
  // opens) — shown as a warning, never a blocker.
  const [labelUsage, setLabelUsage] = useState<{ key: string; names: string[] } | null>(null);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(PRESET_COLORS[0]);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatLabels = useConversationStore((s) => s.chatLabels);
  const deletedChatLabels = useConversationStore((s) => s.deletedChatLabels);
  const updateConvLabel = useConversationStore((s) => s.updateConvLabel);
  const addCustomLabel = useConversationStore((s) => s.addCustomLabel);
  const deleteCustomLabel = useConversationStore((s) => s.deleteCustomLabel);
  const { getLabelPermission, canManageLabels, canDeleteLabels } = usePermissions();

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current?.offsetHeight || 200;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Open upward if not enough space below but enough above
    if (spaceBelow < dropdownHeight + 8 && spaceAbove > dropdownHeight + 8) {
      setPos({ top: rect.top - dropdownHeight - 4, left: rect.left });
    } else {
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, []);

  // Position the dropdown when opened, and re-position after first render to get actual height
  useEffect(() => {
    if (!open) return;
    updatePosition();
    // Re-position after a frame so dropdownRef has its real height
    const raf = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(raf);
  }, [open, updatePosition]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setAdding(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll in any ancestor
  useEffect(() => {
    if (!open) return;
    const handler = () => { setOpen(false); setAdding(false); };
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [open]);

  // Focus input when adding
  useEffect(() => {
    if (adding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [adding]);

  // Escape cancels the delete popup (ConfirmDialog has no key handling).
  useEffect(() => {
    if (!confirmDeleteKey) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setConfirmDeleteKey(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmDeleteKey]);

  // Permission on the label currently applied to this conversation.
  const currentLabelPerm =
    currentLabel && currentLabel !== 'null' ? getLabelPermission(currentLabel) : 'full_access';
  // A 'no_access' (Hide) label must not leak its name/colour even when it's
  // already applied — surface it as "No Label" instead of a visible badge.
  const currentLabelHidden = currentLabelPerm === 'no_access';
  // view_only / no_access on the current label means the user may not change or
  // remove it, so the whole picker (No Label, switching, Add Label) is read-only.
  const currentLabelLocked = currentLabelPerm === 'view_only' || currentLabelPerm === 'no_access';

  // Label creation is governed solely by the group-wide "Manage Labels" toggle
  // (canManageLabels also honours a legacy full_access category default). A
  // stray full_access on a single label no longer grants creation — that was the
  // old, surprising behaviour the toggle replaced.
  const canCreateLabels = canManageLabels;

  // Label resolved from the loaded label set (defaults + custom).
  const knownLabel: ConvChatLabel | null =
    currentLabel && currentLabel !== 'null' && !currentLabelHidden ? chatLabels[currentLabel] || null : null;

  // The conversation has a label stored in Firestore that isn't in the loaded
  // label set — e.g. created in another app/org, or deleted from the org's
  // label list while still applied to this chat. Don't silently show it as
  // "No Label": render it with a grey background so it's visible — under its
  // recorded name when this org deleted it, else the raw key.
  const isOrphanLabel = !!currentLabel && currentLabel !== 'null' && !currentLabelHidden && !knownLabel;
  const deletedLabel = isOrphanLabel ? deletedChatLabels[currentLabel!] : undefined;
  const currentLabelInfo: ConvChatLabel | null = knownLabel || (isOrphanLabel
    ? {
        display_label: deletedLabel?.display_label || currentLabel!.replace(/_/g, ' '),
        value: currentLabel!,
        color: '#6b7280',
      }
    : null);

  // Sort labels by order_pos, filtering out no_access labels
  // 'unreplied' is a virtual computed status (derived from lastMessageIsIncoming)
  // and must never be settable on a conversation — exclude it from the picker.
  const sortedLabels = Object.entries(chatLabels)
    .filter(([key]) => (key !== 'unreplied' && getLabelPermission(key) !== 'no_access'))
    .sort(([, a], [, b]) => (a.order_pos ?? 0) - (b.order_pos ?? 0));

  const handleSelect = (labelKey: string) => {
    // 'unreplied' is a virtual computed status — never set it on a conversation
    if (labelKey === 'unreplied') return;
    updateConvLabel(conversationId, labelKey);
    onChange?.(labelKey);
    setOpen(false);
    setAdding(false);
  };

  // Swap the dropdown for the delete popup and look up the automations that
  // still use the label, so the popup can warn before it's deleted.
  const openDeleteConfirm = (key: string) => {
    setOpen(false);
    setAdding(false);
    setConfirmDeleteKey(key);
    setLabelUsage(null);
    void findAutomationsUsingLabel(key).then((names) => setLabelUsage({ key, names }));
  };

  // The label in the delete popup, and the popup's text: which automations
  // still use it (if any), then what happens to chats already tagged.
  const confirmLabel = confirmDeleteKey ? chatLabels[confirmDeleteKey] : undefined;
  const usageNames = labelUsage && labelUsage.key === confirmDeleteKey ? labelUsage.names : [];
  const confirmDescription = [
    usageNames.length > 0
      ? `Used by ${usageNames.length === 1 ? 'an automation' : `${usageNames.length} automations`}: ${usageNames.slice(0, 3).join(', ')}${usageNames.length > 3 ? ` +${usageNames.length - 3} more` : ''}.`
      : '',
    'Tagged chats keep it until re-tagged.',
  ].filter(Boolean).join(' ');

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
    setAdding(false);
    setConfirmDeleteKey(null); // never reopen with a stale delete-confirm showing
  };

  const handleAddLabel = () => {
    const name = newLabelName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, '_');
    // Prevent creating labels that duplicate system defaults
    const duplicatesSystem = Object.values(chatLabels).some(
      (l) => l.systemClass && l.display_label.toLowerCase() === name.toLowerCase()
    );
    // The key is derived from the name, so also refuse a key that's already taken:
    // a system status key ("Hold" → 'hold' = On Hold) would override the built-in
    // status, and an existing custom key would silently overwrite that label.
    const keyTaken = isSystemLabelKey(key) || Object.prototype.hasOwnProperty.call(chatLabels, key);
    if (duplicatesSystem || keyTaken) {
      toast.error(`A label named "${name}" already exists.`);
      return;
    }
    if (addCustomLabel) {
      addCustomLabel(key, { display_label: name, value: key, color: newLabelColor });
    }
    // Apply the new label to this conversation. isNewLabel bypasses the apply
    // permission gate — the creator just made it, so it has no per-label perm
    // yet and would otherwise resolve to a possibly-restrictive category default.
    updateConvLabel(conversationId, key, { isNewLabel: true });
    onChange?.(key);
    setNewLabelName('');
    setNewLabelColor(PRESET_COLORS[0]);
    setAdding(false);
    setOpen(false);
  };

  return (
    <div ref={triggerRef} className="relative">
      {compact ? (
        // Compact trigger — small badge for conversation list
        <span
          onClick={handleToggle}
          className={cn(
            'inline-flex items-center text-xs font-medium px-1.5 rounded-sm leading-none shrink-0 cursor-pointer h-5',
            'hover:opacity-80 transition-opacity border border-transparent',
            !currentLabelInfo && 'text-muted-foreground hover:text-foreground'
          )}
          style={
            currentLabelInfo
              ? currentLabelInfo.systemClass
                ? { color: currentLabelInfo.color, borderColor: currentLabelInfo.color + '40' }
                : { backgroundColor: currentLabelInfo.color, color: 'white' }
              : undefined
          }
          title={deletedLabel ? `${currentLabelInfo?.display_label} (deleted label)` : currentLabelInfo?.display_label || 'Set label'}
        >
          {currentLabelInfo ? (
            <>
              {currentLabelInfo.systemClass && (
                <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: currentLabelInfo.color }} />
              )}
              {currentLabelInfo.display_label}
            </>
          ) : (
            <>
              <Tag className="h-3 w-3 mr-0.5" />
              No Label
            </>
          )}
        </span>
      ) : (
        // Full trigger — button with label text for chat header
        <button
          data-testid="chat-label-dropdown"
          onClick={handleToggle}
          className={cn(
            'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors',
            'hover:bg-muted/50',
            currentLabelInfo
              ? 'border-current/20'
              : 'border-border text-muted-foreground'
          )}
          style={
            currentLabelInfo
              ? currentLabelInfo.systemClass
                ? { color: currentLabelInfo.color, borderColor: currentLabelInfo.color + '40' }
                : { backgroundColor: currentLabelInfo.color, color: 'white', borderColor: currentLabelInfo.color }
              : undefined
          }
        >
          <Tag className="h-3 w-3" />
          <span>{currentLabelInfo?.display_label || 'No Label'}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      )}

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] rounded-lg border bg-popover shadow-lg"
          style={{ top: pos.top, left: pos.left, width: 180, padding: '4px 0' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* No Label option */}
          {(() => {
            const isNoLabel = !currentLabel || currentLabel === 'null';
            return (
              <button
                onClick={() => { if (!currentLabelLocked) handleSelect('null'); }}
                disabled={currentLabelLocked}
                className={cn(
                  'w-full text-left flex items-center transition-colors',
                  currentLabelLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted',
                  isNoLabel ? 'bg-primary/5 font-medium' : ''
                )}
                style={{ padding: '6px 12px', fontSize: '13px', gap: '8px' }}
              >
                <span className="rounded-full bg-muted-foreground/30 shrink-0" style={{ width: 8, height: 8 }} />
                <span className="flex-1">No Label</span>
                {currentLabelLocked && <Lock style={{ width: 12, height: 12 }} className="text-muted-foreground shrink-0" />}
                {isNoLabel && !currentLabelLocked && <Check style={{ width: 14, height: 14 }} className="text-primary shrink-0" />}
              </button>
            );
          })()}

          <div className="border-t" style={{ margin: '4px 0' }} />

          {sortedLabels.map(([key, label]) => {
            const isSelected = currentLabel === key;
            const perm = getLabelPermission(key);
            const isViewOnly = perm === 'view_only';
            // Lock every option when the current label can't be modified —
            // switching away would remove a label the user has no rights over.
            const disabled = isViewOnly || (currentLabelLocked && !isSelected);
            // Only CUSTOM labels can be deleted, only with the Delete Labels
            // permission, and never on a row that shows the lock (`disabled` covers a
            // view_only label and a locked current label; no_access labels never
            // reach this list). Keyed on the system key set, not the stored
            // systemClass, so a custom label can't pass for a built-in status.
            const canDelete = canDeleteLabels && !isSystemLabelKey(key) && !disabled;
            return (
              <div
                key={key}
                className={cn(
                  'flex items-center transition-colors',
                  isSelected && 'bg-primary/5',
                  // A disabled (locked/view-only) row is dimmed and never highlights.
                  disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted'
                )}
              >
                <button
                  onClick={() => !disabled && handleSelect(key)}
                  disabled={disabled}
                  title={label.display_label}
                  className={cn(
                    'flex-1 min-w-0 text-left flex items-center',
                    disabled && 'cursor-not-allowed',
                    isSelected && 'font-medium'
                  )}
                  style={{ padding: '6px 12px', fontSize: '13px', gap: '8px' }}
                >
                  <span
                    className="rounded-full shrink-0"
                    style={{ width: 8, height: 8, backgroundColor: label.color }}
                  />
                  <span className="flex-1 min-w-0 break-words">{label.display_label}</span>
                  {disabled && <Lock style={{ width: 12, height: 12 }} className="text-muted-foreground shrink-0" />}
                  {isSelected && !disabled && <Check style={{ width: 14, height: 14 }} className="text-primary shrink-0" />}
                </button>
                {canDelete && (
                  <button
                    onClick={() => openDeleteConfirm(key)}
                    title="Delete label"
                    aria-label={`Delete label ${label.display_label}`}
                    className="shrink-0 text-muted-foreground/50 hover:text-red-600 transition-colors"
                    style={{ padding: '6px 10px' }}
                  >
                    <Trash2 style={{ width: 13, height: 13 }} />
                  </button>
                )}
              </div>
            );
          })}

          <div className="border-t" style={{ margin: '4px 0' }} />

          {/* Add custom label — only for users who can Manage (create) labels,
              and never when the current label is locked (creating + applying
              would overwrite it). */}
          {(!canCreateLabels || currentLabelLocked) ? null : !adding ? (
            <button
              onClick={(e) => { e.stopPropagation(); setAdding(true); }}
              className="w-full text-left hover:bg-muted flex items-center text-primary transition-colors"
              style={{ padding: '6px 12px', fontSize: '13px', gap: '8px' }}
            >
              <Plus style={{ width: 14, height: 14 }} className="shrink-0" />
              <span>Add Label</span>
            </button>
          ) : (
            <div style={{ padding: '6px 12px' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-1.5 mb-2">
                <span
                  className="rounded-full shrink-0 cursor-pointer border border-border"
                  style={{ width: 14, height: 14, backgroundColor: newLabelColor }}
                />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Label name"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddLabel(); if (e.key === 'Escape') setAdding(false); }}
                  className="flex-1 min-w-0 bg-transparent border border-border rounded px-1.5 py-0.5 text-xs outline-none focus:border-primary"
                />
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {PRESET_COLORS.map((c) => (
                  <span
                    key={c}
                    onClick={() => setNewLabelColor(c)}
                    className={cn(
                      'rounded-full cursor-pointer border-2 transition-colors',
                      newLabelColor === c ? 'border-foreground' : 'border-transparent'
                    )}
                    style={{ width: 14, height: 14, backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="flex gap-1.5">
                <button
                  onClick={handleAddLabel}
                  disabled={!newLabelName.trim()}
                  className="flex-1 text-xs bg-primary text-primary-foreground rounded px-2 py-1 hover:bg-primary/90 disabled:opacity-50"
                >
                  Add
                </button>
                <button
                  onClick={() => setAdding(false)}
                  className="text-xs text-muted-foreground rounded px-2 py-1 hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      {confirmLabel && createPortal(
        // Portalled like the dropdown, and clicks are stopped here: the picker can
        // sit inside a clickable conversation row, and React events bubble
        // through portals.
        <div className="relative z-[10000]" onClick={(e) => e.stopPropagation()}>
          <ConfirmDialog
            open
            onClose={() => setConfirmDeleteKey(null)}
            onConfirm={() => { deleteCustomLabel(confirmDeleteKey!); setConfirmDeleteKey(null); }}
            title={`Delete “${confirmLabel.display_label}”?`}
            description={confirmDescription}
            confirmText="Delete"
            variant="destructive"
          />
        </div>,
        document.body
      )}
    </div>
  );
}
